import React, { useRef, useState, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import api from '../services/api';
import { Box, Typography, Button, CircularProgress, Paper, Alert, Chip } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { overlaySx } from '../styles/uiTokens';

const MODEL_URL = '/models';

function PontoCamera({ onPontoRegistrado }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  const [status, setStatus] = useState('loading');
  const [match, setMatch] = useState(null);
  const [erro, setErro] = useState('');
  const [registrando, setRegistrando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [tentativas, setTentativas] = useState(0);
  const [tipoConfirmado, setTipoConfirmado] = useState(null);

  const startVideo = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      setErro('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
      setStatus('error');
    }
  }, []);

  const loadModels = useCallback(async () => {
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setStatus('ready');
    } catch (err) {
      console.error('Erro ao carregar modelos:', err);
      setErro('Erro ao carregar modelos de reconhecimento facial. Verifique a conexão.');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    loadModels();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadModels]);

  useEffect(() => {
    if (status !== 'ready') return;

    let cancelled = false;

    const initCamera = async () => {
      await new Promise((r) => setTimeout(r, 100));
      if (!cancelled) await startVideo();
    };
    initCamera();

    return () => { cancelled = true; };
  }, [status, startVideo]);

  const onVideoLoaded = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
  }, []);

  const desenharDeteccao = useCallback((detection) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const displaySize = { width: video.videoWidth, height: video.videoHeight };
    faceapi.matchDimensions(canvas, displaySize);
    const resized = faceapi.resizeResults(detection, displaySize);
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    faceapi.draw.drawDetections(canvas, resized);
    faceapi.draw.drawFaceLandmarks(canvas, resized);
  }, []);

  const tentarReconhecer = useCallback(async () => {
    if (registrando || !videoRef.current || videoRef.current.readyState < 2) return;

    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (!detection) {
        setMatch(null);
        setTentativas((t) => t + 1);
        return;
      }

      desenharDeteccao(detection);

      const descriptors = Array.from(detection.descriptor);

      const response = await api.post('/ponto/reconhecer', { descriptors });

      if (response.data.match) {
        setMatch(response.data.match);
        setTipoConfirmado(response.data.match.tipo);
      } else {
        setMatch(null);
        setTentativas((t) => t + 1);
      }
    } catch (err) {
      console.error('Erro ao reconhecer:', err);
    }
  }, [registrando, desenharDeteccao]);

  useEffect(() => {
    if (status !== 'ready') return;

    intervalRef.current = setInterval(tentarReconhecer, 1500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [status, tentarReconhecer]);

  const handleConfirmar = async () => {
    if (!match || registrando) return;
    setRegistrando(true);
    try {
      const canvas = canvasRef.current;
      const foto_base64 = canvas ? canvas.toDataURL('image/jpeg', 0.8) : null;

      const response = await api.post('/ponto', {
        pessoa_id: match.pessoa_id,
        tipo: tipoConfirmado,
        foto_base64,
      });

      setResultado({
        ...response.data,
        nome: match.nome,
      });
      setStatus('success');
      if (onPontoRegistrado) onPontoRegistrado();
    } catch (err) {
      console.error('Erro ao registrar ponto:', err);
      setErro('Erro ao registrar o ponto. Tente novamente.');
    } finally {
      setRegistrando(false);
    }
  };

  const handleRecusar = () => {
    setMatch(null);
    setTipoConfirmado(null);
    setTentativas(0);
  };

  const handleReiniciar = () => {
    setResultado(null);
    setMatch(null);
    setTipoConfirmado(null);
    setErro('');
    setTentativas(0);
    setStatus('ready');
  };

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }}>Carregando modelos de reconhecimento...</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Isso pode levar alguns segundos na primeira vez.
        </Typography>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{erro}</Alert>
        <Button variant="contained" onClick={handleReiniciar}>
          Tentar novamente
        </Button>
      </Box>
    );
  }

  if (status === 'success' && resultado) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
        <Typography variant="h5" sx={{ mt: 2 }}>
          {resultado.nome}
        </Typography>
        <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
          {resultado.tipo === 'entrada' ? 'ENTRADA' : 'SAÍDA'} registrada!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {resultado.data_hora}
        </Typography>
        <Button variant="contained" sx={{ mt: 3 }} onClick={handleReiniciar} size="large">
          Novo registro
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 500,
          aspectRatio: '3/4',
          bgcolor: 'common.black',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          onLoadedData={onVideoLoaded}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)',
          }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            transform: 'scaleX(-1)',
          }}
        />

        {!match && (
          <Box
            sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', py: 1, ...overlaySx }}
          >
            <Typography variant="body2">
              Posicione seu rosto em frente à câmera
            </Typography>
            <Typography variant="caption" color="grey.400">
              {tentativas > 0 ? `Tentativas: ${tentativas} — Se não reconhecer, use o modo manual` : ''}
            </Typography>
          </Box>
        )}
      </Box>

      {match ? (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            width: '100%',
            maxWidth: 500,
            textAlign: 'center',
            bgcolor: 'success.light',
            color: 'white',
          }}
        >
          <Typography variant="h5">{match.nome}</Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            Registrar{' '}
            <Chip
              label={tipoConfirmado === 'entrada' ? 'ENTRADA' : 'SAÍDA'}
              color={tipoConfirmado === 'entrada' ? 'success' : 'warning'}
              size="medium"
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
            ?
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => setTipoConfirmado((t) => (t === 'entrada' ? 'saida' : 'entrada'))}
            startIcon={<SwapHorizIcon />}
            sx={{ color: 'white', mt: 0.5, opacity: 0.85 }}
          >
            Alternar para {tipoConfirmado === 'entrada' ? 'SAÍDA' : 'ENTRADA'}
          </Button>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleConfirmar}
              disabled={registrando}
              startIcon={registrando ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              Confirmar
            </Button>
            <Button variant="outlined" color="inherit" size="large" onClick={handleRecusar}>
              Não sou eu
            </Button>
          </Box>
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">
          <VideocamIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Aguardando reconhecimento facial...
        </Typography>
      )}

      {erro && (
        <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: 500 }}>
          {erro}
        </Alert>
      )}
    </Box>
  );
}

export default PontoCamera;
